import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { type Components, Virtuoso } from 'react-virtuoso';
import SearchInput from '@/components/common/search-input';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import SearchListItem from '@/components/search/search-list-item';
import useFuseSearch from '@/hooks/search/use-fuse-search';
import useCreateTag from '@/hooks/tags/use-create-tag';
import useDeleteTags from '@/hooks/tags/use-delete-tags';
import { useTagsWithRemarkName } from '@/hooks/tags/use-tags';
import type { Tag } from '@/models/user/tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

const components: Components<Tag> = {
  EmptyPlaceholder: () => <EmptyLayout label='contact.tags.no-content' />,
};

export default function ContactTagsIndexRoute() {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  const { data: tags = [] } = useTagsWithRemarkName();
  const { mutateAsyncWithPrompt: promptCreateTag } = useCreateTag();
  const { mutateAsync: deleteTags } = useDeleteTags();
  const filteredTags = useFuseSearch({
    data: tags,
    keyword,
    keys: ['TagName'],
    initialReturnAll: true,
  });

  const handleSelectTag = (tagId: number) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((id) => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleDeleteTags = async () => {
    await makePrompt({
      key: 'delete-tags',
      type: 'dialog',
      title: t('contact.tags.delete.delete-tag'),
      content: t('contact.tags.delete.confirm-message'),
      promise: () => deleteTags(selectedTags),
    });
    setSelectedTags([]);
    setIsEditing(false);
  };

  return (
    <PageLayout
      title={t('contact.tags.title')}
      className='pb-safe-offset-4'
      headerConfig={{
        navigateBack: true,
        actions: !isEmpty(tags) && (
          <button
            type='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content layout-compact:text-primary'
            onClick={() => {
              setIsEditing(!isEditing);
              setSelectedTags([]);
            }}>
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
        ),
        elementAfterNav: !isEmpty(tags) && (
          <SearchInput
            className='mx-5'
            keyword={keyword}
            setKeyword={setKeyword}
            clearKeyword={() => setKeyword('')}
          />
        ),
      }}>
      <div className='flex flex-col flex-1 '>
        <Virtuoso<Tag>
          className='flex-1'
          data={filteredTags}
          components={components}
          itemContent={(_, tag) => {
            const isSelected = selectedTags.includes(tag.Id);
            const content = (
              <SearchListItem
                className='min-h-12 pl-1'
                avatarImageUrl=''
                titleClassName='desktop:text-xs text-[1.0625rem]'
                title={`${tag.TagName} (${tag.TagCount})`}
                description={tag.TagMember.map(({ Name, RemarkName }) => RemarkName || Name).join(', ')}
                highlight={keyword}
                onClick={() => handleSelectTag(tag.Id)}
                suffix={
                  <input
                    type='checkbox'
                    readOnly
                    checked={isSelected}
                    className={cn(' rounded-full border-base-200 radio-primary radio pointer-events-none', {
                      hidden: !isEditing,
                    })}
                  />
                }
              />
            );

            return isEditing ? (
              content
            ) : (
              <Link to={ROUTES.CONTACT.TAGS.BY_ID.$buildPath({ params: { tagId: tag.Id } })}>{content}</Link>
            );
          }}
        />
      </div>

      <button
        type='button'
        className='btn btn-primary btn-md mx-5 desktop:mb-4'
        disabled={isEditing && isEmpty(selectedTags)}
        onClick={() => (isEditing ? handleDeleteTags() : promptCreateTag(true))}>
        {isEditing ? `${t('common.delete')} (${selectedTags.length})` : t('common.add')}
      </button>
    </PageLayout>
  );
}
